import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { useLotSizingPurchaseOrderDraft } from '@/features/lot-sizing/lot-sizing-purchase-order-draft-context';
import {
  type CreatePurchaseOrderFormValues,
  createPurchaseOrderSchema,
} from '@/features/lot-sizing/schema/create-purchase-order-schema';
import { generateId } from '@/lib/id';
import { getOrderedQuantityError } from '@/services/apis/errors/ordered-quantity-error';
import { getProductionDeclarationDataError } from '@/services/apis/errors/production-declaration-data-error';
import { getSupplierLeadTimeError } from '@/services/apis/errors/supplier-lead-time-error';
import { useCreatePurchaseOrder } from '@/services/apis/mutations/purchase-orders/use-create-purchase-order';
import type { LotSizing } from '@/types/core/lot-sizing/lot-sizing';

const productWeightValidationErrorSchema = z.object({
  errors: z.object({
    productWeight: z.array(z.string()).min(1),
  }),
});
const productWeightReferencesRegex = / SKUs: (\d+(?:, \d+)*)\.$/;

export function useCreatePurchaseOrderAction(lotSizing: LotSizing) {
  const { t } = useTranslation('lotSizing');
  const navigate = useNavigate();
  const { mutate, isPending } = useCreatePurchaseOrder();
  const { hasQuantityErrors } = useLotSizingPurchaseOrderDraft();

  const values = useMemo(
    (): CreatePurchaseOrderFormValues => ({
      lotSizing: {
        id: lotSizing.id,
        suppliers: lotSizing.suppliers.map((s) => ({
          lotSizingId: lotSizing.id,
          minOrderAmount: s.minOrderAmount,
          currentLotSizingAmount: s.currentLotSizingAmount,
        })),
      },
    }),
    [lotSizing],
  );

  const { handleSubmit, formState, trigger } =
    useForm<CreatePurchaseOrderFormValues>({
      resolver: zodResolver(createPurchaseOrderSchema),
      values,
      mode: 'onChange',
    });

  useEffect(() => {
    if (values) {
      trigger();
    }
  }, [trigger, values]);

  const canCreate = formState.isValid && !hasQuantityErrors;

  const onSubmit = (data: CreatePurchaseOrderFormValues) => {
    if (hasQuantityErrors) {
      return;
    }
    const lotSizingId = data.lotSizing.id;
    mutate(
      { lotSizingId },
      {
        onSuccess: (purchaseOrders) => {
          if (purchaseOrders.length === 1) {
            navigate({
              to: '/purchase-orders/$id',
              params: { id: String(purchaseOrders[0].id) },
            });
          } else {
            const filters = [
              {
                id: 'lotSizingId' as const,
                value: lotSizingId,
                variant: 'text' as const,
                operator: 'eq' as const,
                filterId: generateId({ length: 8 }),
              },
            ];
            navigate({
              to: `/purchase-orders?filters=${encodeURIComponent(JSON.stringify(filters))}`,
            });
          }
        },
        onError: (error) => {
          let description = t('createPurchaseOrderError');

          if (
            axios.isAxiosError<unknown>(error) &&
            error.response?.status === 422
          ) {
            const validationError =
              productWeightValidationErrorSchema.safeParse(error.response.data);

            if (validationError.success) {
              description = t('createPurchaseOrderProductWeightError');
              const referenceLists =
                validationError.data.errors.productWeight.map(
                  (message) =>
                    message
                      .match(productWeightReferencesRegex)?.[1]
                      ?.split(', ') ?? [],
                );

              if (referenceLists.every((references) => references.length > 0)) {
                const references = [...new Set(referenceLists.flat())];
                description = t(
                  'createPurchaseOrderProductWeightReferencesError',
                  {
                    references: references.join(', '),
                    count: references.length,
                  },
                );
              }
            }
          }

          toast.error(t('error', { ns: 'common' }), {
            description:
              getOrderedQuantityError(error, t) ??
              getSupplierLeadTimeError(error, t) ??
              getProductionDeclarationDataError(error, t) ??
              description,
            duration: Number.POSITIVE_INFINITY,
          });
        },
      },
    );
  };

  const validationErrorKey = useMemo(() => {
    const supplierErrors = formState.errors.lotSizing?.suppliers;
    const supplierItemMessage = Array.isArray(supplierErrors)
      ? supplierErrors.find(
          (supplierError): supplierError is { message: string } =>
            typeof supplierError?.message === 'string',
        )?.message
      : null;

    return [
      formState.errors.lotSizing?.message,
      formState.errors.lotSizing?.suppliers?.message,
      formState.errors.lotSizing?.suppliers?.root?.message,
      supplierItemMessage,
    ].find((msg): msg is string => typeof msg === 'string');
  }, [formState.errors]);

  return {
    handleSubmit,
    onSubmit,
    isPending,
    canCreate,
    validationErrorKey,
  };
}
